import { useQueries } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";

import LoadingScreen from "@/components/loading-screen";
import { clearStoredBoostBillingAddress } from "@/features/boosts/hooks/useBoostBillingAddress";
import { clearStoredPendingBoost } from "@/features/boosts/hooks/useBoostRecovery";
import { useGdprTracking } from "@/hooks/useGdprTracking";
import { setUserToMonitor } from "@/monitoring";
import { getRedirectUrl } from "@/utils/redirect";

import { revokeOAuthToken, verifyCredentials } from "./api";
import AuthContext, { type SwitchAccountOpts } from "./auth-context";
import { AuthQueryKeys } from "./query-keys";
import {
  DEPRECATED_AUTH_KEY,
  LOCAL_STORAGE_AUTH_KEY,
  LOCAL_STORAGE_EXTERNAL_APP_KEY,
  LOCAL_STORAGE_EXTERNAL_BASE_URL_KEY,
  LOCAL_STORAGE_EXTERNAL_SCOPES_KEY,
  cacheAccount,
  cacheAccountUrl,
  getAccountFromIndexedDb,
  getCachedAccountApp,
  getCachedAccountUrl,
  getCachedAccountsArray,
  uncacheAccount,
} from "./utils";

import type { Account, App } from "@/schemas";
import type { ApiErrorResponse } from "@/types/api";

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { targeting, maskIdentifier } = useGdprTracking();
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);

  const isLoggedIn = !!currentAccount;

  const otherAccounts = accounts.filter(
    (accountToFilter) => accountToFilter.id !== currentAccount?.id,
  );

  function setCurrentAccountFromIndexDb() {
    getAccountFromIndexedDb().then(setCurrentAccount).catch(console.error);
  }

  function addAccount(token: string, accountToAdd: Account, app: App) {
    cacheAccount(accountToAdd, token, app);
  }

  function updateCurrentAccount(accountToUpdate: Account) {
    setCurrentAccount(accountToUpdate);
    cacheAccount(accountToUpdate);
  }

  async function switchAccount(
    accountToSwitchTo: Account,
    opts: SwitchAccountOpts = {},
  ) {
    const { shouldRedirect = true } = opts;

    cacheAccountUrl(accountToSwitchTo.url);

    // The sessionStorage entry survives a `shouldRedirect: false` switch, which
    // does not reload, so clearing it here is required.
    clearStoredBoostBillingAddress();
    clearStoredPendingBoost();

    if (shouldRedirect) {
      const redirectUri = getRedirectUrl();
      return location.replace(redirectUri);
    }
  }

  async function logOut() {
    // Send request to revoke the oauth token.
    try {
      await revokeOAuthToken(getCachedAccountApp());
    } catch (error: any) {
      console.error(error);
    }

    const otherAccount = otherAccounts.find(
      (otherAccount) => otherAccount.id !== currentAccount?.id,
    );

    // Billing PII belongs to the departing user, not the device.
    clearStoredBoostBillingAddress();
    clearStoredPendingBoost();

    if (otherAccount) {
      // keep them signed in
      uncacheAccount(currentAccount!, otherAccount);

      location.replace("/");
      return;
    }

    // Clear all stored cache from React Query
    setUserToMonitor(null);
    localStorage.removeItem(DEPRECATED_AUTH_KEY);
    localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);
    localStorage.removeItem(LOCAL_STORAGE_EXTERNAL_BASE_URL_KEY);
    localStorage.removeItem(LOCAL_STORAGE_EXTERNAL_APP_KEY);
    localStorage.removeItem(LOCAL_STORAGE_EXTERNAL_SCOPES_KEY);
    window.location.replace("/");
  }

  const queryResults = useQueries({
    queries: getCachedAccountsArray().map((user) => {
      return {
        queryKey: AuthQueryKeys.user(user.id),
        onError: (error: ApiErrorResponse) => {
          // TODO: 401, 403 -- delete token / user -- not entire auth key
          if (error.response?.status === 401) {
            localStorage.removeItem(DEPRECATED_AUTH_KEY);
            localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);
          }
        },
        queryFn: async () => {
          const account = await verifyCredentials(user.access_token);

          cacheAccount(account, user.access_token);
          setAccounts((prevAccounts) => [...prevAccounts, account]);

          if (account.url === getCachedAccountUrl()) {
            setCurrentAccount(account);
          }

          return account;
        },
      };
    }),
  });

  const value = useMemo(
    () => ({
      currentAccount,
      addAccount,
      isLoggedIn,
      logOut,
      otherAccounts,
      switchAccount,
      updateCurrentAccount,
    }),
    [currentAccount, otherAccounts],
  );

  useEffect(() => {
    setCurrentAccountFromIndexDb();
  }, []);

  useEffect(() => {
    if (currentAccount) {
      setUserToMonitor({
        username: targeting
          ? currentAccount.username
          : maskIdentifier(currentAccount.username),
        id: currentAccount.id,
      });
    }
  }, [currentAccount, targeting]);

  // Once we set the currentAccount from the "useQueries" hook, we can
  // render the UI.
  if (!currentAccount && queryResults.some((result) => result.isLoading)) {
    return <LoadingScreen />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
